import { styled, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import RowLink from "_shared/_views/tables/row-link";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
} from "material-react-table";
import { ToolbarButtonsContainer } from "permissions/_views/tables/permissions-table-styles";
import {
  MobileToolbarButtonsContainer,
  searchFieldSpacing,
  ScrollableTabs,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLoaderData, useNavigate } from "react-router";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  USER_COWORKER_QUERY_MAXIMUM_LENGTH,
  UserCoworkerOrderField,
  UserCoworkerOrderParameters,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetUserCoworkersQuery } from "users/information/coworker-information/_api/user-coworker-api";
import UserCoworker from "users/information/coworker-information/user-coworker";
import { PageTab } from "users/networking/_views/network-page-tabs";

const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(() => ({
  minWidth: 0,
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

const columnHelper = createMRTColumnHelper<UserCoworker>();

export function createCoworkerFilters(name?: string, email?: string): UserCoworkerFilterParameters {
  return new UserCoworkerFilterParameters(name, email);
}

export function isCoworkerGlobalFilterValid(value: string): boolean {
  return value.length <= USER_COWORKER_QUERY_MAXIMUM_LENGTH;
}

export default function CoworkersTab() {
  const handledRef = React.useRef<string | null>(null);

  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // Route
  const routeParams = useLoaderData();
  const routeAction = routeParams?.action as string | undefined;
  const routeId = routeParams?.id as string | undefined;

  // #region Queries
  const nameFilter = columnFilters.find((f) => f.id === "name")?.value as string | undefined;
  const emailFilter = columnFilters.find((f) => f.id === "email")?.value as string | undefined;
  const isGlobalFilterValid = isCoworkerGlobalFilterValid(globalFilter);

  const query = useMemo(() => {
    if (!isGlobalFilterValid) return undefined;

    const paginationParameters = PaginationParameters.create(pagination);
    // The Legal Entity column contains a display name, while the API accepts only a legal-entity
    // Guid. Do not turn that display value into an invalid API filter.
    const filterParameters = createCoworkerFilters(nameFilter, emailFilter);

    const orderParameters = new UserCoworkerOrderParameters(
      (sorting[0]?.id as UserCoworkerOrderField) ?? UserCoworkerOrderField.Name,
      sorting[0]?.desc ? OrderDirection.Descending : OrderDirection.Ascending,
    );

    return new UserCoworkerQuery(filterParameters, orderParameters, paginationParameters, globalFilter);
  }, [pagination, nameFilter, emailFilter, sorting, globalFilter, isGlobalFilterValid]);

  const coworkerQuery = useGetUserCoworkersQuery(
    query ? { query, append: false } : skipToken,
  );

  const coworkers = useMemo(() => {
    if (!coworkerQuery.data) {
      return PaginatedResponse.empty<UserCoworker>();
    }
    return PaginatedResponse.from<UserCoworker>(
      UserCoworker.fromJSONArray(coworkerQuery.data.data),
      coworkerQuery.data.headers,
    );
  }, [coworkerQuery.data]);

  const handleViewProfile = useCallback(
    (individualId?: Guid, data?: UserCoworker) => {
      if (!individualId || !data) return;

      const individualProfile = new IndividualProfile({
        userId: data.id,
        firstName: data.name.firstName ?? "",
        lastName: data.name.lastName ?? "",
        avatarId: data.avatarId,
        legalEntityId: data.legalEntityId,
        email: data.email,
      });

      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        onClose: () => {
          const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);
          navigate(parentPath.pathname);
        },
        component: <ViewIndividualProfile individualId={individualId} profile={individualProfile} />,
      });
    },
    [navigate, openDialog],
  );

  // #region Lifecycle
  useEffect(() => {
    if (routeAction !== "view" || !routeId) return;

    const key = `${routeAction}:${routeId}`;
    if (handledRef.current === key) return;

    const match = coworkers.data.find((c) => c.individualId?.value === routeId);
    if (!match) return;

    handledRef.current = key;
    handleViewProfile(new Guid(routeId), match);
  }, [routeAction, routeId, coworkers.data, handleViewProfile]);
  // #endregion

  const renderContactNameColumn = useCallback(
    (user: UserCoworker) => {
      return (
        <NameCellContainer>
          <IndividualAvatar individual={user.asIndividual() ?? null} />
          <RowTitle>
            <RowLink to={`/network/coworkers/view/${user.individualId}`}>{user.name.fullName}</RowLink>
          </RowTitle>
        </NameCellContainer>
      );
    },
    [],
  );

  const columns = useMemo(() => [
    columnHelper.display({
      header: "Name",
      id: "name",
      Cell: ({ row }) => renderContactNameColumn(row.original),
    }),
    columnHelper.display({
      header: "Legal Entity",
      id: "entityName",
      Cell: ({ row }) => row.original.entityName,
    }),
  ], [renderContactNameColumn]);

  const tableOptions: MRT_TableOptions<UserCoworker> = {
    columns,
    data: coworkers.data,
    getRowId: (row: UserCoworker) => row.individualId?.value ?? row.id?.value ?? "",
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: false,
    enableColumnPinning: true,

    renderToolbarInternalActions: ({ table }) =>
      !isMediumDisplaySize && (
        <ToolbarButtonsContainer>
          <ToolbarButtons>
            <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
            <ToggleFiltersButton table={table} />
            <ShowHideColumnsButton table={table} />
            <ToggleFullScreenButton table={table} />
          </ToolbarButtons>
        </ToolbarButtonsContainer>
      ),
    renderTopToolbarCustomActions: ({ table }) => (
      <TableTopToolbarContainer>
        <TableTitle variant="h4">Network</TableTitle>

        <LowerTopToolbarContainer>
          <ScrollableTabs
            value={PageTab.Coworkers}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            onChange={(_event, newValue) => navigate(`/network/${newValue}`)}
          >
            <Tab value={PageTab.Contacts} label="Contacts" />
            <Tab value={PageTab.Coworkers} label="Coworkers" />
            <Tab value={PageTab.Pending} label="Pending" />
            <Tab value={PageTab.Groups} label="Groups" />
          </ScrollableTabs>
        </LowerTopToolbarContainer>

        {isMediumDisplaySize && (
          <MobileToolbarButtonsContainer>
            <GlobalFilterTextField table={table} />
            <ToolbarButtons>
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </MobileToolbarButtonsContainer>
        )}
      </TableTopToolbarContainer>
    ),

    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) => ({
      ...searchTextFieldProps({
        searches: "coworkers",
        table,
        maxLength: USER_COWORKER_QUERY_MAXIMUM_LENGTH,
      }),
      error: !isGlobalFilterValid,
      helperText: !isGlobalFilterValid
        ? `Search must be ${USER_COWORKER_QUERY_MAXIMUM_LENGTH} characters or fewer.`
        : undefined,
    }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: coworkerQuery.isError,
      isLoading: coworkerQuery.isFetching,
      onRetry: () => void coworkerQuery.refetch(),
      subject: "coworkers",
    }),

    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableStickyHeader: true,
    enableStickyFooter: true,

    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => { navigate(`/network/coworkers/view/${row.original.individualId}`); },
      sx: { cursor: "pointer" },
    }),

    muiTableContainerProps: { sx: { flex: 1 } },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: { sx: { height: "100%", display: "flex", flexDirection: "column" } },

    muiToolbarAlertBannerProps: coworkerQuery.isError
      ? { color: "error" as const, children: "Error loading data" }
      : undefined,

    rowCount: coworkers.totalElements,

    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,

    state: {
      globalFilter,
      columnFilters,
      isLoading: coworkerQuery.isLoading,
      pagination,
      showAlertBanner: coworkerQuery.isError,
      showProgressBars: coworkerQuery.isFetching,
      showSkeletons: coworkerQuery.isLoading,
      sorting,
    },
  };

  return <MRTNoMemo<UserCoworker> options={tableOptions} />;
}
